import dayjs from 'dayjs';

import type {
  PredictionVoucherBundle,
  PredictionVoucherBundleResponse,
  PredictionVoucherRule,
  PredictionVoucherRulePayload,
  PredictionVoucherTier,
  PredictionVoucherTierDraft,
  PredictionVoucherTierDraftMap,
  PredictionVoucherTierMap,
  VoucherChannel,
} from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function ensureArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

export function toNumber(value?: unknown) {
  const num = Number(value ?? 0);

  return Number.isFinite(num) ? num : 0;
}

export function formatDateInput(value?: string | null) {
  if (!value) return '';

  const date = dayjs(value);

  return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : String(value);
}

export function toApiDateTime(value?: string | null) {
  if (!value) return '';

  const date = dayjs(value);

  return date.isValid() ? date.toISOString() : String(value);
}

export function getDefaultRule(): PredictionVoucherRule {
  return {
    perUserDailyCap: 100,
    perUserEventCap: 5000,
    eventTotalCap: 1000000,
    totalBudgetEnforced: true,
    effectiveFrom: '2026-06-11T00:00:00Z',
    effectiveTo: '2026-07-21T06:00:00Z',
  };
}

export function getDefaultTierMap(): PredictionVoucherTierMap {
  return {
    futures: [
      { tierOrder: 1, volumeThresholdU: '5000', voucherCount: 1 },
      { tierOrder: 2, volumeThresholdU: '50000', voucherCount: 10 },
      { tierOrder: 3, volumeThresholdU: '150000', voucherCount: 50 },
    ],
    predictionMarket: [
      { tierOrder: 1, volumeThresholdU: '5000', voucherCount: 1 },
      { tierOrder: 2, volumeThresholdU: '50000', voucherCount: 10 },
      { tierOrder: 3, volumeThresholdU: '150000', voucherCount: 50 },
    ],
    predictionLeverage: [
      { tierOrder: 1, volumeThresholdU: '5000', voucherCount: 1 },
      { tierOrder: 2, volumeThresholdU: '50000', voucherCount: 10 },
      { tierOrder: 3, volumeThresholdU: '150000', voucherCount: 50 },
    ],
  };
}

export function getDefaultBundle(): PredictionVoucherBundle {
  const tiers = getDefaultTierMap();

  return {
    rule: getDefaultRule(),
    tiers,
    summary: {
      futuresTierCount: tiers.futures.length,
      predictionMarketTierCount: tiers.predictionMarket.length,
      predictionLeverageTierCount: tiers.predictionLeverage.length,
    },
  };
}

export function normalizeRule(raw: unknown): PredictionVoucherRule {
  const fallback = getDefaultRule();

  if (!isRecord(raw)) return fallback;

  return {
    perUserDailyCap: toNumber(
      raw.perUserDailyCap ?? raw.per_user_daily_cap ?? fallback.perUserDailyCap
    ),
    perUserEventCap: toNumber(
      raw.perUserEventCap ?? raw.per_user_event_cap ?? fallback.perUserEventCap
    ),
    eventTotalCap: toNumber(raw.eventTotalCap ?? raw.event_total_cap ?? fallback.eventTotalCap),
    totalBudgetEnforced:
      typeof raw.totalBudgetEnforced === 'boolean'
        ? raw.totalBudgetEnforced
        : typeof raw.total_budget_enforced === 'boolean'
          ? raw.total_budget_enforced
          : fallback.totalBudgetEnforced,
    effectiveFrom: String(raw.effectiveFrom ?? raw.effective_from ?? fallback.effectiveFrom),
    effectiveTo: String(raw.effectiveTo ?? raw.effective_to ?? fallback.effectiveTo),
    configVersion:
      raw.configVersion === undefined && raw.config_version === undefined
        ? undefined
        : toNumber(raw.configVersion ?? raw.config_version),
    updatedBy:
      raw.updatedBy !== undefined
        ? String(raw.updatedBy)
        : raw.updated_by !== undefined
          ? String(raw.updated_by)
          : undefined,
    updatedAt:
      raw.updatedAt !== undefined
        ? String(raw.updatedAt)
        : raw.updated_at !== undefined
          ? String(raw.updated_at)
          : undefined,
  };
}

export function normalizeTier(raw: unknown): PredictionVoucherTier {
  if (!isRecord(raw)) {
    return {
      tierOrder: 1,
      volumeThresholdU: '0',
      voucherCount: 0,
    };
  }

  return {
    tierOrder:
      raw.tierOrder === undefined && raw.tier_order === undefined
        ? undefined
        : toNumber(raw.tierOrder ?? raw.tier_order),
    volumeThresholdU: String(raw.volumeThresholdU ?? raw.volume_threshold_u ?? '0'),
    voucherCount: toNumber(raw.voucherCount ?? raw.voucher_count),
  };
}

export function normalizeTierMap(raw: unknown): PredictionVoucherTierMap {
  const fallback = getDefaultTierMap();

  if (!isRecord(raw)) return fallback;

  return {
    futures: ensureArray<unknown>(raw.futures).map(normalizeTier),
    predictionMarket: ensureArray<unknown>(raw.predictionMarket ?? raw.prediction_market).map(
      normalizeTier
    ),
    predictionLeverage: ensureArray<unknown>(
      raw.predictionLeverage ?? raw.prediction_leverage
    ).map(normalizeTier),
  };
}

export function normalizeBundle(
  raw: PredictionVoucherBundleResponse | unknown
): PredictionVoucherBundle {
  const source = isRecord(raw) && isRecord(raw.data) ? raw.data : raw;

  if (!isRecord(source)) {
    return getDefaultBundle();
  }

  const tiers = normalizeTierMap(source.tiers);

  return {
    rule: normalizeRule(source.rule),
    tiers,
    summary: {
      futuresTierCount: toNumber(
        isRecord(source.summary)
          ? (source.summary.futuresTierCount ?? source.summary.futures_tier_count)
          : tiers.futures.length
      ),
      predictionMarketTierCount: toNumber(
        isRecord(source.summary)
          ? (source.summary.predictionMarketTierCount ??
              source.summary.prediction_market_tier_count)
          : tiers.predictionMarket.length
      ),
      predictionLeverageTierCount: toNumber(
        isRecord(source.summary)
          ? (source.summary.predictionLeverageTierCount ??
              source.summary.prediction_leverage_tier_count)
          : tiers.predictionLeverage.length
      ),
    },
  };
}

export function toDraftRows(tiers: PredictionVoucherTier[], channel: VoucherChannel) {
  return tiers.map((tier, index) => ({
    ...tier,
    localId: `${channel}-${Date.now()}-${index}`,
  }));
}

export function toDraftMap(tiers: PredictionVoucherTierMap): PredictionVoucherTierDraftMap {
  return {
    futures: toDraftRows(tiers.futures, 'futures'),
    predictionMarket: toDraftRows(tiers.predictionMarket, 'predictionMarket'),
    predictionLeverage: toDraftRows(tiers.predictionLeverage, 'predictionLeverage'),
  };
}

export function createEmptyTier(prev?: PredictionVoucherTierDraft): PredictionVoucherTierDraft {
  const nextOrder = prev ? toNumber(prev.tierOrder) + 1 : 1;
  const nextThreshold = prev ? Math.max(toNumber(prev.volumeThresholdU) * 2, 0) : 0;

  return {
    localId: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    tierOrder: nextOrder,
    volumeThresholdU: String(nextThreshold),
    voucherCount: 0,
  };
}

export function normalizeDraftRows(rows: PredictionVoucherTierDraft[]) {
  return rows
    .map((row) => ({
      volumeThresholdU: String(row.volumeThresholdU || '0'),
      voucherCount: toNumber(row.voucherCount),
    }))
    .sort((a, b) => toNumber(a.volumeThresholdU) - toNumber(b.volumeThresholdU));
}

export function buildPayload(
  rule: PredictionVoucherRule,
  tiers: PredictionVoucherTierDraftMap
): PredictionVoucherRulePayload {
  return {
    rule: {
      perUserDailyCap: toNumber(rule.perUserDailyCap),
      perUserEventCap: toNumber(rule.perUserEventCap),
      eventTotalCap: toNumber(rule.eventTotalCap),
      totalBudgetEnforced: Boolean(rule.totalBudgetEnforced),
      effectiveFrom: toApiDateTime(rule.effectiveFrom),
      effectiveTo: toApiDateTime(rule.effectiveTo),
    },
    tiers: {
      futures: normalizeDraftRows(tiers.futures),
      predictionMarket: normalizeDraftRows(tiers.predictionMarket),
      predictionLeverage: normalizeDraftRows(tiers.predictionLeverage),
    },
  };
}

export function isBundleEqual(
  leftRule: PredictionVoucherRule,
  leftTiers: PredictionVoucherTierDraftMap,
  rightRule: PredictionVoucherRule,
  rightTiers: PredictionVoucherTierDraftMap
) {
  return (
    JSON.stringify(buildPayload(leftRule, leftTiers)) ===
    JSON.stringify(buildPayload(rightRule, rightTiers))
  );
}

export function getErrorMessage(error: unknown) {
  if (!error) return '';

  if (typeof error === 'string') return error;

  if (error instanceof Error) return error.message;

  if (isRecord(error)) {
    if (typeof error.message === 'string') return error.message;
    if (typeof error.error === 'string') return error.error;

    if (isRecord(error.response) && isRecord(error.response.data)) {
      const data = error.response.data;

      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  return String(error);
}
